import { useMemo } from 'react';
import { TourType } from '@/Components/Tour/Tour';
import { useLocalization } from '@/Hooks';
import { logisticSettingsActions } from '@/Pages/Settings/modules/LogisticsSettings/services/reducer';
import { AppDispatch } from '@/Redux/ConfigureStore';
import { useDispatch } from 'react-redux';
import { CountryCode, LogisticType } from '@/Enums';

export function useLogisticSettings() {
  const dispatch: AppDispatch = useDispatch();

  const {
    tours: {
      LogisticSettings: { PageTour, LogisticTour, ConnectTour, AccountSettingsTour },
    },
  } = useLocalization();

  const pageTour = useMemo<TourType>(
    () => ({
      id: 'pageTour',
      title: PageTour.Title,
      steps: [
        {
          content: PageTour.Step1.Text,
          placement: 'auto',
          target: '#logisticTitle',
          title: PageTour.Step1.Title,
          disableBeacon: true,
          link: PageTour.Step1.DetailsLink,
        },
        {
          content: PageTour.Step2.Text,
          placement: 'auto',
          target: '#logisticDropDown',
          title: PageTour.Step2.Title,
          disableBeacon: true,
          link: PageTour.Step2.DetailsLink,
          styles: {
            spotlight: {
              minHeight: '110px',
            },
          },
        },
        {
          content: PageTour.Step3.Text,
          placement: 'auto',
          target: '#contractsContainer',
          title: PageTour.Step3.Title,
          disableBeacon: true,
          link: PageTour.Step3.DetailsLink,
        },
        {
          content: PageTour.Step4.Text,
          placement: 'auto',
          target: '#connectContract',
          title: PageTour.Step4.Title,
          disableBeacon: true,
          link: PageTour.Step4.DetailsLink,
        },
      ],
    }),
    [PageTour],
  );

  const logisticTour = useMemo<TourType>(
    () => ({
      id: 'logisticTour',
      title: LogisticTour.Title,
      before: () => {
        dispatch(
          logisticSettingsActions.setLogisticInfoModal({
            visible: true,
            data: {
              logo: '',
              logisticApiType: LogisticType.Dpd,
              address: 'Kaiserstr 19h, 76646 BRUCHSAL,',
              country: CountryCode.DE,
              mandatId: 1,
              mandatName: 'Manual',
              warehouseId: 1,
              warehouseName: 'Default warehouse',
              contracts: [{ id: 1, name: 'Default', isActive: true }],
              logisticTypeName: 'DPD',
              genLogisticTypeId: 1,
            },
          }),
        );
      },
      after: () => {
        dispatch(logisticSettingsActions.setLogisticInfoModal({ visible: false }));
      },
      steps: [
        {
          content: LogisticTour.Step1.Text,
          placement: 'auto',
          target: '#logisticModalTitle',
          title: LogisticTour.Step1.Title,
          disableBeacon: true,
          link: LogisticTour.Step1.DetailsLink,
        },
        {
          content: LogisticTour.Step2.Text,
          placement: 'auto',
          target: '#logisticInfo',
          title: LogisticTour.Step2.Title,
          disableBeacon: true,
          link: LogisticTour.Step2.DetailsLink,
        },
        {
          content: LogisticTour.Step3.Text,
          placement: 'auto',
          target: '#contractsList',
          title: LogisticTour.Step3.Title,
          disableBeacon: true,
          link: LogisticTour.Step3.DetailsLink,
        },
        {
          content: LogisticTour.Step4.Text,
          placement: 'auto',
          target: '#contractActions',
          title: LogisticTour.Step4.Title,
          disableBeacon: true,
          link: LogisticTour.Step4.DetailsLink,
        },
        {
          content: LogisticTour.Step5.Text,
          placement: 'auto',
          target: '#addContract',
          title: LogisticTour.Step5.Title,
          disableBeacon: true,
          link: LogisticTour.Step5.DetailsLink,
        },
      ],
    }),
    [LogisticTour],
  );

  const connectTour = useMemo<TourType>(
    () => ({
      id: 'connectTour',
      title: ConnectTour.Title,
      before: () => {
        dispatch(logisticSettingsActions.setConnectModal({ visible: true, connectStep: 'select' }));
      },
      after: () => {
        dispatch(logisticSettingsActions.setConnectModal({ visible: false }));
      },
      steps: [
        {
          content: ConnectTour.Step1.Text,
          placement: 'auto',
          target: '#connectLogisticType',
          title: ConnectTour.Step1.Title,
          disableBeacon: true,
          link: ConnectTour.Step1.DetailsLink,
        },
      ],
    }),
    [ConnectTour],
  );

  const accountSettingsTour = useMemo<TourType>(
    () => ({
      id: 'accountSettingsTour',
      title: AccountSettingsTour.Title,
      before: () => {
        dispatch(
          logisticSettingsActions.setContractModal({
            visible: true,
            locationId: 1,
            mandatId: 1,
            logisticType: LogisticType.Dpd,
            logisticTypeId: 1,
          }),
        );
      },
      after: () => {
        dispatch(logisticSettingsActions.setContractModal({ visible: true }));
      },
      steps: [
        {
          content: AccountSettingsTour.Step1.Text,
          placement: 'auto',
          target: '#accountSettings',
          title: AccountSettingsTour.Step1.Title,
          disableBeacon: true,
          link: AccountSettingsTour.Step1.DetailsLink,
          callback: () => dispatch(logisticSettingsActions.setContractActiveStep(0)),
        },
        {
          content: AccountSettingsTour.Step2.Text,
          placement: 'auto',
          target: '#accountName',
          title: AccountSettingsTour.Step2.Title,
          disableBeacon: true,
          link: AccountSettingsTour.Step2.DetailsLink,
          callback: () => dispatch(logisticSettingsActions.setContractActiveStep(0)),
        },
        {
          content: AccountSettingsTour.Step3.Text,
          placement: 'auto',
          target: '#accountFields',
          title: AccountSettingsTour.Step3.Title,
          disableBeacon: true,
          link: AccountSettingsTour.Step3.DetailsLink,
          callback: () => dispatch(logisticSettingsActions.setContractActiveStep(0)),
        },
        {
          content: AccountSettingsTour.Step4.Text,
          placement: 'auto',
          target: '#checkConnection',
          title: AccountSettingsTour.Step4.Title,
          disableBeacon: true,
          link: AccountSettingsTour.Step4.DetailsLink,
          callback: () => dispatch(logisticSettingsActions.setContractActiveStep(0)),
        },
        {
          content: AccountSettingsTour.Step5.Text,
          placement: 'auto',
          target: '#shippingService',
          title: AccountSettingsTour.Step5.Title,
          disableBeacon: true,
          link: AccountSettingsTour.Step5.DetailsLink,
          callback: () => dispatch(logisticSettingsActions.setContractActiveStep(1)),
        },
        {
          content: AccountSettingsTour.Step6.Text,
          placement: 'auto',
          target: '#shippingCurier',
          title: AccountSettingsTour.Step6.Title,
          disableBeacon: true,
          link: AccountSettingsTour.Step6.DetailsLink,
          callback: () => dispatch(logisticSettingsActions.setContractActiveStep(1)),
        },
        {
          content: AccountSettingsTour.Step7.Text,
          placement: 'auto',
          target: '#shippingPickup',
          title: AccountSettingsTour.Step7.Title,
          disableBeacon: true,
          link: AccountSettingsTour.Step7.DetailsLink,
          callback: () => dispatch(logisticSettingsActions.setContractActiveStep(1)),
        },
        {
          content: AccountSettingsTour.Step8.Text,
          placement: 'auto',
          target: '#shippingCalendar',
          title: AccountSettingsTour.Step8.Title,
          disableBeacon: true,
          link: AccountSettingsTour.Step8.DetailsLink,
          callback: () => dispatch(logisticSettingsActions.setContractActiveStep(1)),
        },

        {
          content: AccountSettingsTour.Step9.Text,
          placement: 'auto',
          target: '#printerSettings',
          title: AccountSettingsTour.Step9.Title,
          disableBeacon: true,
          link: AccountSettingsTour.Step9.DetailsLink,
          callback: () => dispatch(logisticSettingsActions.setContractActiveStep(2)),
        },
        {
          content: AccountSettingsTour.Step10.Text,
          placement: 'auto',
          target: '#labelFormat',
          title: AccountSettingsTour.Step10.Title,
          disableBeacon: true,
          link: AccountSettingsTour.Step10.DetailsLink,
          callback: () => dispatch(logisticSettingsActions.setContractActiveStep(2)),
        },
        {
          content: AccountSettingsTour.Step11.Text,
          placement: 'auto',
          target: '#referenceType',
          title: AccountSettingsTour.Step11.Title,
          disableBeacon: true,
          link: AccountSettingsTour.Step11.DetailsLink,
          callback: () => dispatch(logisticSettingsActions.setContractActiveStep(2)),
        },
        {
          content: AccountSettingsTour.Step12.Text,
          placement: 'auto',
          target: '#orderMarker',
          title: AccountSettingsTour.Step12.Title,
          disableBeacon: true,
          link: AccountSettingsTour.Step12.DetailsLink,
          callback: () => dispatch(logisticSettingsActions.setContractActiveStep(2)),
        },
      ],
    }),
    [AccountSettingsTour],
  );

  return [pageTour, logisticTour, connectTour, accountSettingsTour];
}
